import { useState, useEffect, type FormEvent } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { ApiClientError } from '../api/client.js';
import { patchProject, type Project } from '../api/projects.js';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-l)' },
  // Ver CreateProjectDialog: el <form> va con `display: contents` para no
  // interponer un contenedor flex entre la superficie y el DialogBody. Así el
  // cuerpo hace scroll y el pie de acciones nunca queda fuera de pantalla al
  // hacer zoom (125%+) o en viewports chicos (bug de WebKit con grid dentro de
  // un flex item dimensionado solo por max-height).
  formReset: { display: 'contents' },
});

interface Props {
  project: Project;
  trigger: React.ReactElement;
  onUpdated: (p: Project) => void;
}

/**
 * Editar nombre y descripción de un proyecto ya creado.
 * Complementa a CreateProjectDialog: si el usuario se equivocó al crear la obra,
 * puede corregirla desde el workspace sin recrearla.
 */
export function EditProjectDialog({ project, trigger, onUpdated }: Props) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sincronizar los campos con el proyecto al abrir (por si cambió por fuera).
  useEffect(() => {
    if (!open) return;
    setName(project.name);
    setDescription(project.description ?? '');
    setError(null);
  }, [open, project.name, project.description]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const updated = await patchProject(project.id, {
        name: name.trim(),
        description: description.trim() || null,
      });
      onUpdated(updated);
      setOpen(false);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError('Error inesperado');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => setOpen(d.open)}>
      <DialogTrigger disableButtonEnhancement>{trigger}</DialogTrigger>
      <DialogSurface>
        <form className={styles.formReset} onSubmit={onSubmit}>
          <DialogBody>
            <DialogTitle>Editar proyecto</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                {error && (
                  <MessageBar intent="error">
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}
                <Field label="Nombre" required>
                  <Input
                    value={name}
                    onChange={(_, d) => setName(d.value)}
                    autoFocus
                    disabled={submitting}
                    placeholder="ej: Carretera tramo 2 — Licitación 2026"
                  />
                </Field>
                <Field label="Descripción" hint="Opcional">
                  <Textarea
                    value={description}
                    onChange={(_, d) => setDescription(d.value)}
                    disabled={submitting}
                    rows={4}
                    placeholder="Notas internas, cliente, fechas clave…"
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" type="button" disabled={submitting}>
                  Cancelar
                </Button>
              </DialogTrigger>
              <Button appearance="primary" type="submit" disabled={submitting || !name.trim()}>
                {submitting ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}
